import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const Hero3D = lazy(() => import("./Hero3D"));

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      <div className="absolute inset-0">
        <Suspense fallback={<div className="w-full h-full bg-[#050505]" />}>
          <Hero3D />
        </Suspense>
      </div>

      {/* Dark gradient overlay for legibility */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/80" />

      {/* Top corner ticker */}
      <div className="hidden md:block absolute top-24 md:top-28 left-6 md:left-10 z-10 font-mono-accent text-[11px] tracking-[0.3em] uppercase text-white/50">
        <div>— NaN Studio · Est. 2019</div>
        <div className="mt-1">Tirana / Remote</div>
      </div>

      <div className="hidden md:block absolute top-24 md:top-28 right-6 md:right-10 z-10 font-mono-accent text-[11px] tracking-[0.3em] uppercase text-white/50 text-right">
        <div>N 41.3275°</div>
        <div className="mt-1">E 19.8187°</div>
      </div>

      {/* Center / Bottom typography */}
      <div className="relative z-10 h-full w-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-mono-accent text-[11px] tracking-[0.3em] uppercase text-white/60 mb-6"
        >
          (01) — Engineering digital reality
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black uppercase leading-[0.85] tracking-tighter text-white text-[14vw] md:text-[10vw] lg:text-[8.5vw]"
        >
          We build<br />
          <span className="text-[#29ABE2]">the</span> impossible.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-white/70 text-base md:text-lg leading-relaxed">
            A small, ruthless studio shipping web apps, mobile products, AI
            systems and custom SaaS for founders who refuse to look like
            everyone else.
          </p>

          <a
            href="#services"
            data-testid="hero-scroll-cta"
            className="group inline-flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/70 hover:text-white transition-colors"
          >
            Scroll to explore
            <ArrowDownRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </a>
        </motion.div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/[0.06] bg-black/40 backdrop-blur-md overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-3 font-mono-accent text-[11px] tracking-[0.35em] uppercase text-white/40">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-6 shrink-0">
              <span>· Web Apps</span>
              <span>· Mobile</span>
              <span>· AI Integration</span>
              <span>· Custom SaaS</span>
              <span>· CRM Platforms</span>
              <span>· Internal Tools</span>
              <span>· Design Systems</span>
              <span>· Three.js / WebGL</span>
              <span>· Web Apps</span>
              <span>· Mobile</span>
              <span>· AI Integration</span>
              <span>· Custom SaaS</span>
              <span>· CRM Platforms</span>
              <span>· Internal Tools</span>
              <span>· Design Systems</span>
              <span>· Three.js / WebGL</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
